import React from 'react'
import { useIntl } from 'react-intl'

import AddressWithViewMapButton from './Common/AddressWithViewMapButton'
import ConfirmationDetails from './Common/ConfirmationDetails'
import Divider from './Common/Divider'
import ItemTypeAndBookedLabel from './Common/ItemTypeAndBookedLabel'
import SummaryAdditionalNotes from './Common/SummaryAdditionalNotes'
import SummaryDataItem from './Common/SummaryDataItem'
import SummaryDateTime from './Common/SummaryDateTime'
import SummaryImage from './Common/SummaryImage'
import DefaultSummaryModalFooter from './Common/SummaryModalFooter/DefaultSummaryModalFooter'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalImageThumbnail from 'LuxKit/Modal/ModalImageThumbnail'
import GooglePlaceRating from 'tripPlanner/components/GooglePlaceRating/GooglePlaceRating'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import type { EventItem } from 'tripPlanner/types/tripItem'
import { maybePlaceId } from 'tripPlanner/utils'
import { getDisplayableCustomThingToDoName } from 'tripPlanner/utils/itemConversions/common'

interface Props {
  item: EventItem
}

function EventItemSummary(props: Props) {
  const { item } = props
  const intl = useIntl()

  const { closeModal, goBack } = useModalCallbacks()
  const placeId = maybePlaceId(item.place)

  const reviewsLabel = intl.formatMessage({
    id: 'BpIVMadJJC',
    defaultMessage: 'Reviews',
    description: 'Label for the Google reviews section in event trip item summary',
  })
  const websiteLabel = intl.formatMessage({
    id: 'XjOEk1Y8JS',
    defaultMessage: 'Website',
    description: 'Label for the website link in event trip item summary',
  })
  const phoneNumberLabel = intl.formatMessage({
    id: 'Qgda1Dy9Cr',
    defaultMessage: 'Phone number',
    description: 'Label for the phone number in event trip item summary',
  })

  return (
    <>
      <ModalHeader
        title={getDisplayableCustomThingToDoName(item)}
        subtitle={item.place.name}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />

      {item.place.type === 'GEO' && item.place.photoUrl && (
        <ModalImageThumbnail
          image={{
            url: item.place.photoUrl,
            id: item.id,
            title: item.place.name,
          }}
        />
      )}

      <ModalBody>
        <SummaryImage
          place={item.place}
          tripId={item.tripId}
          tripItemId={item.id}
        />
        <ModalContent>
          <VerticalSpacer gap={20}>
            <ItemTypeAndBookedLabel item={item} />
            {(item.startDate || item.startDay) && (
              <SummaryDateTime
                date={item.startDate}
                time={item.startTime}
                day={item.startDay}
              />
            )}
            {item.place.address && (
              <AddressWithViewMapButton
                item={item}
                address={item.place.address}
              />
            )}
            {placeId && (
              <GooglePlaceRating size="M" placeId={placeId} label={reviewsLabel} />
            )}
            <ConfirmationDetails
              isBooked={item.isBooked}
              confirmationCode={item.confirmationCode}
              price={item.price}
            />
            <Divider />
            <SummaryDataItem title={websiteLabel} value={item.url} url={item.url} />
            <SummaryDataItem
              title={phoneNumberLabel}
              value={item.phone}
              url={`tel:${item.phone}`}
            />
            <SummaryAdditionalNotes notes={item.notes} />
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>

      <DefaultSummaryModalFooter item={item} />
    </>
  )
}

export default EventItemSummary
